import React from 'react';
import {
    SimpleGrid,
    Text,
    Paper,
    Badge,
    Box,
    Tabs,
    ActionIcon,
    Tooltip,
    Title,
    Group,
    Button
} from '@mantine/core';
import { useMantineColorScheme } from '@mantine/core';
import { IconCopy, IconBinaryTree, IconArrowsMaximize, IconArrowsMinimize, IconDownload } from '@tabler/icons-react';
import { ScimReferenceDisplay } from './ScimReferenceDisplay';
import { ReferencesGraph } from './ReferencesGraph';
import { getReferenceDisplay } from '../../types/ReferenceTypes';
// react-syntax-highlighter: Syntax-highlighted JSON/code for entity attribute values in detail content.
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { copyToClipboard } from '../../utils/clipboardUtils';
import { getAxiosInstance } from '../../services/axiosInstance';
import { entityService } from '../../services/entityServiceSingleton';
import { debugLog } from '../../utils/debugLog';

export interface EntityDetailContentProps {
    entity: any | null;
}

export const EntityDetailContent: React.FC<EntityDetailContentProps> = ({
    entity
}) => {
    const { colorScheme } = useMantineColorScheme();
    const [activeTab, setActiveTab] = React.useState<string | null>('summary');
    const [sqlCopied, setSqlCopied] = React.useState(false);
    const [sqlExpanded, setSqlExpanded] = React.useState(false);
    const [isDownloadingFile, setIsDownloadingFile] = React.useState(false);
    const [isFileDownloadFallbackReady, setIsFileDownloadFallbackReady] = React.useState(false);
    const fileDownloadFallbackTimerRef = React.useRef<number | null>(null);
    const fileDownloadInFlightRef = React.useRef(false);
    const fileDownloadPrimaryUsedRef = React.useRef(false);

    const handleCopySqlStatement = async (sqlStatement: string) => {
        try {
            await copyToClipboard(sqlStatement);
            setSqlCopied(true);
            setTimeout(() => setSqlCopied(false), 2000);
            debugLog('✅ SQL statement copied to clipboard');
        } catch (error) {
            console.error('❌ Failed to copy SQL statement to clipboard:', error);
        }
    };

    const handleCopyJson = async () => {
        try {
            const seen = new WeakSet();
            const replacer = (_key: string, value: any) => {
                if (typeof value === 'object' && value !== null) {
                    if (seen.has(value)) {
                        return '[Circular Reference]';
                    }
                    seen.add(value);
                }
                if (typeof value === 'function') return '[Function]';
                if (typeof value === 'undefined') return '[Undefined]';
                if (typeof value === 'symbol') return '[Symbol]';
                return value;
            };

            const jsonString = JSON.stringify(entity, replacer, 2);
            await copyToClipboard(jsonString);
            debugLog('✅ JSON copied to clipboard');
        } catch (error) {
            console.error('❌ Failed to copy JSON to clipboard:', error);
        }
    };

    const renderField = (value: any): string => {
        if (value === null || value === undefined) return 'Not specified';
        if (typeof value === 'string') return value;
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    };

    const renderReferenceField = (value: any) => {
        return <ScimReferenceDisplay reference={value} variant="default" showType={true} />;
    };

    const renderReferenceArray = (values: any[], label: string) => {
        if (!values?.length) return null;

        return (
            <Box>
                <Text size="sm" c="dimmed">{label}</Text>
                <Group gap="xs" wrap="wrap">
                    {values.map((value, index) => (
                        <ScimReferenceDisplay
                            key={index}
                            reference={value}
                            variant="compact"
                            showType={true}
                        />
                    ))}
                </Group>
                <Box mt="xs" p="xs" bg="yellow.1" style={{ borderRadius: 4 }}>
                    <Text size="xs" c="dimmed">
                        Debug: {values.length} {label.toLowerCase()}(s) found
                    </Text>
                </Box>
            </Box>
        );
    };

    const renderStringArray = (values: string[], label: string) => {
        if (!values?.length) return null;

        return (
            <Box>
                <Text size="sm" c="dimmed">{label}</Text>
                <Group gap="xs" wrap="wrap">
                    {values.map((value, index) => (
                        <Badge key={index} variant="light" size="sm">{value}</Badge>
                    ))}
                </Group>
            </Box>
        );
    };

    const renderBooleanField = (value: boolean, label: string) => {
        return (
            <Box>
                <Text size="sm" c="dimmed">{label}</Text>
                <Badge color={value ? 'green' : 'gray'} variant="light" size="sm">
                    {value ? 'Yes' : 'No'}
                </Badge>
            </Box>
        );
    };

    const base64ToBytes = (base64: string): Uint8Array => {
        const binary = window.atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes;
    };

    const inferMimeTypeFromFilename = (filename: string | null | undefined): string => {
        const name = (filename ?? '').toLowerCase();
        if (name.endsWith('.xml')) return 'application/xml';
        if (name.endsWith('.json')) return 'application/json';
        if (name.endsWith('.txt') || name.endsWith('.log')) return 'text/plain';
        if (name.endsWith('.csv')) return 'text/csv';
        if (name.endsWith('.html') || name.endsWith('.htm')) return 'text/html';
        return 'application/octet-stream';
    };

    const getBestFileFilename = (): string => {
        if (typeof entity?.FileName === 'string' && entity.FileName.trim()) return entity.FileName.trim();
        return 'download.bin';
    };

    const getFileDownloadInfo = (): { filename: string; bytes: Uint8Array; mimeType: string } | null => {
        // EntityType is a reference object for most entities; File entity has Data as base64.
        const entityTypeId = entity?.EntityType?.Id ?? entity?.EntityType?.id ?? entity?.EntityType;
        if (String(entityTypeId ?? '').toLowerCase() !== 'file') return null;
        if (typeof entity?.Data !== 'string' || entity.Data.trim() === '') return null;
        const filename = getBestFileFilename();
        const explicitContentType =
            (typeof entity?.ContentType === 'string' && entity.ContentType.trim())
                ? entity.ContentType.trim()
                : (typeof entity?.contentType === 'string' && entity.contentType.trim())
                    ? entity.contentType.trim()
                    : null;
        const mimeType = explicitContentType ?? inferMimeTypeFromFilename(filename);
        const bytes = base64ToBytes(entity.Data.trim());
        return { filename, bytes, mimeType };
    };

    const getFileDownloadMeta = (): { fileId: string | null; filename: string; mimeType: string } | null => {
        const entityTypeId = entity?.EntityType?.Id ?? entity?.EntityType?.id ?? entity?.EntityType;
        if (String(entityTypeId ?? '').toLowerCase() !== 'file') return null;
        const filename = getBestFileFilename();
        const explicitContentType =
            (typeof entity?.ContentType === 'string' && entity.ContentType.trim())
                ? entity.ContentType.trim()
                : (typeof entity?.contentType === 'string' && entity.contentType.trim())
                    ? entity.contentType.trim()
                    : null;
        const mimeType = explicitContentType ?? inferMimeTypeFromFilename(filename);
        const fileId = entity?.Id ?? entity?.id ?? null;
        return { fileId: fileId != null ? String(fileId) : null, filename, mimeType };
    };

    const bytesToBlobPart = (bytes: Uint8Array): ArrayBuffer => {
        // TS lib.dom's BlobPart typing is picky about ArrayBuffer vs SharedArrayBuffer.
        // Slice produces a real ArrayBuffer containing exactly the bytes.
        return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    };

    const handleDownloadFileData = async () => {
        // Ref-based lock so rapid multi-clicks can't slip in before state applies.
        if (fileDownloadInFlightRef.current || isDownloadingFile || fileDownloadPrimaryUsedRef.current) return;
        const meta = getFileDownloadMeta();
        if (!meta) return;

        const fileId = meta.fileId;
        fileDownloadInFlightRef.current = true;
        fileDownloadPrimaryUsedRef.current = true; // one-shot primary download
        setIsDownloadingFile(true);
        setIsFileDownloadFallbackReady(false);

        if (fileDownloadFallbackTimerRef.current != null) {
            window.clearTimeout(fileDownloadFallbackTimerRef.current);
        }

        const downloadBlob = (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = meta.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        };

        const runFallback = async () => {
            try {
                if (!fileId) {
                    // No id: try existing in-memory Data if present; otherwise nothing we can do.
                    const info = getFileDownloadInfo();
                    if (!info) return;
                    const blob = new Blob([bytesToBlobPart(info.bytes)], { type: info.mimeType });
                    downloadBlob(blob);
                    return;
                }

                // Old fallback: call the Data-attribute API, then client-side decode it.
                const result = await entityService.getEntityById(String(fileId), 'File', false, ['Data']);
                const data = (result as any)?.Data ?? (result as any)?.data;
                if (typeof data === 'string' && data.trim()) {
                    const bytes = base64ToBytes(data.trim());
                    const blob = new Blob([bytesToBlobPart(bytes)], { type: meta.mimeType });
                    downloadBlob(blob);
                    return;
                }

                // Last resort: use any already-loaded Data value
                const info = getFileDownloadInfo();
                if (!info) return;
                const blob = new Blob([bytesToBlobPart(info.bytes)], { type: info.mimeType });
                downloadBlob(blob);
            } catch {
                const info = getFileDownloadInfo();
                if (!info) return;
                const blob = new Blob([bytesToBlobPart(info.bytes)], { type: info.mimeType });
                downloadBlob(blob);
            }
        };

        try {
            // Start $download immediately.
            const serverDownloadPromise = (async () => {
                if (!fileId) throw new Error('Missing file id');
                const axios = getAxiosInstance();
                const resp = await axios.get(`/entities/files/${encodeURIComponent(String(fileId))}/$download`, {
                    responseType: 'blob',
                });
                return resp.data as Blob;
            })();

            // After a couple seconds, show a fallback action the user can click.
            fileDownloadFallbackTimerRef.current = window.setTimeout(() => {
                setIsFileDownloadFallbackReady(true);
            }, 2000);

            const blob = await serverDownloadPromise;
            if (fileDownloadFallbackTimerRef.current != null) {
                window.clearTimeout(fileDownloadFallbackTimerRef.current);
                fileDownloadFallbackTimerRef.current = null;
            }
            // If fallback became visible while waiting, hide it on success.
            setIsFileDownloadFallbackReady(false);
            downloadBlob(blob);
        } catch {
            if (fileDownloadFallbackTimerRef.current != null) {
                window.clearTimeout(fileDownloadFallbackTimerRef.current);
                fileDownloadFallbackTimerRef.current = null;
            }
            setIsFileDownloadFallbackReady(true);
        } finally {
            setIsDownloadingFile(false);
            fileDownloadInFlightRef.current = false;
        }
    };

    if (!entity) {
        return null;
    }

    return (
        <Box>
            <Tabs value={activeTab} onChange={setActiveTab} mb="md">
                <Tabs.List>
                    <Tabs.Tab value="summary">Summary</Tabs.Tab>
                    <Tabs.Tab value="graph" leftSection={<IconBinaryTree size={16} />}>
                        References Graph
                    </Tabs.Tab>
                    <Tabs.Tab value="raw">
                        <Group gap="xs">
                            Raw Data
                            {activeTab === 'raw' && (
                                <Tooltip label="Copy JSON to clipboard">
                                    <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopyJson();
                                        }}
                                    >
                                        <IconCopy size={14} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Group>
                    </Tabs.Tab>
                </Tabs.List>
            </Tabs>

            {activeTab === 'summary' && (
                <Box>
                    {/* Basic Information */}
                    <Title order={5} mb="xs">Basic Information</Title>
                    <Paper p="md" mb="md" withBorder>
                        {getFileDownloadMeta() && (
                            <Box mb="md">
                                <Group gap="xs" wrap="wrap">
                                    {fileDownloadPrimaryUsedRef.current ? (
                                        <Text size="sm" c="dimmed">
                                            Downloaded
                                        </Text>
                                    ) : (
                                        <Button
                                            size="xs"
                                            variant="light"
                                            leftSection={<IconDownload size={14} />}
                                            onClick={handleDownloadFileData}
                                            disabled={isDownloadingFile}
                                        >
                                            Download Data
                                        </Button>
                                    )}
                                    {isFileDownloadFallbackReady && (
                                        <Button
                                            size="xs"
                                            variant="subtle"
                                            onClick={() => void (async () => {
                                                const meta = getFileDownloadMeta();
                                                if (!meta) return;
                                                setIsDownloadingFile(true);
                                                try {
                                                    if (meta.fileId) {
                                                        const result = await entityService.getEntityById(String(meta.fileId), 'File', false, ['Data']);
                                                        const data = (result as any)?.Data ?? (result as any)?.data;
                                                        if (typeof data === 'string' && data.trim()) {
                                                            const bytes = base64ToBytes(data.trim());
                                                            const blob = new Blob([bytesToBlobPart(bytes)], { type: meta.mimeType });
                                                            const url = URL.createObjectURL(blob);
                                                            const a = document.createElement('a');
                                                            a.href = url;
                                                            a.download = meta.filename;
                                                            document.body.appendChild(a);
                                                            a.click();
                                                            a.remove();
                                                            setTimeout(() => URL.revokeObjectURL(url), 1000);
                                                            return;
                                                        }
                                                    }
                                                } finally {
                                                    setIsDownloadingFile(false);
                                                }
                                            })()}
                                        >
                                            Fallback download
                                        </Button>
                                    )}
                                    {(isDownloadingFile || isFileDownloadFallbackReady) && (
                                        <Text size="xs" c="dimmed">
                                            {isFileDownloadFallbackReady ? 'Fallback ready' : 'Downloading…'}
                                        </Text>
                                    )}
                                </Group>
                            </Box>
                        )}
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                            {entity.Id && (
                                <Box>
                                    <Text size="sm" c="dimmed">ID</Text>
                                    <Text ff="monospace">{entity.Id}</Text>
                                </Box>
                            )}
                            {entity.DisplayName && (
                                <Box>
                                    <Text size="sm" c="dimmed">Display Name</Text>
                                    <Text>{entity.DisplayName}</Text>
                                </Box>
                            )}
                            {entity.Name && (
                                <Box>
                                    <Text size="sm" c="dimmed">Name</Text>
                                    <Text>{entity.Name}</Text>
                                </Box>
                            )}
                            {entity.Period && (
                                <Box>
                                    <Text size="sm" c="dimmed">Period</Text>
                                    <Text>{entity.Period}</Text>
                                </Box>
                            )}
                            {entity.Room && (
                                <Box>
                                    <Text size="sm" c="dimmed">Room</Text>
                                    <Text>{entity.Room}</Text>
                                </Box>
                            )}
                            {entity.Address && (
                                <Box>
                                    <Text size="sm" c="dimmed">Address</Text>
                                    <Text>{entity.Address}</Text>
                                </Box>
                            )}
                            {entity.City && (
                                <Box>
                                    <Text size="sm" c="dimmed">City</Text>
                                    <Text>{entity.City}</Text>
                                </Box>
                            )}
                            {entity.State && (
                                <Box>
                                    <Text size="sm" c="dimmed">State</Text>
                                    <Text>{entity.State}</Text>
                                </Box>
                            )}
                            {entity.Zip && (
                                <Box>
                                    <Text size="sm" c="dimmed">Zip</Text>
                                    <Text>{entity.Zip}</Text>
                                </Box>
                            )}
                            {entity.PhoneNumber && (
                                <Box>
                                    <Text size="sm" c="dimmed">Phone</Text>
                                    <Text>{entity.PhoneNumber}</Text>
                                </Box>
                            )}
                            {entity.Email && (
                                <Box>
                                    <Text size="sm" c="dimmed">Email</Text>
                                    <Text>{entity.Email}</Text>
                                </Box>
                            )}
                            {entity.Description && (
                                <Box>
                                    <Text size="sm" c="dimmed">Description</Text>
                                    <Text>{entity.Description}</Text>
                                </Box>
                            )}
                            {entity.UserName && (
                                <Box>
                                    <Text size="sm" c="dimmed">Username</Text>
                                    <Text>{entity.UserName}</Text>
                                </Box>
                            )}
                            {entity.StartDate && (
                                <Box>
                                    <Text size="sm" c="dimmed">Start Date</Text>
                                    <Text>{entity.StartDate}</Text>
                                </Box>
                            )}
                            {entity.EndDate && (
                                <Box>
                                    <Text size="sm" c="dimmed">End Date</Text>
                                    <Text>{entity.EndDate}</Text>
                                </Box>
                            )}
                        </SimpleGrid>
                    </Paper>

                    {/* Reference Fields */}
                    {(entity.School || entity.Rc || entity.Manager || entity.Assistant || entity.Teacher || entity.Principal || entity.Principals || entity.Owner || entity.Members || entity.Member) && (
                        <>
                            <Title order={5} mb="xs">References</Title>
                            <Paper p="md" mb="md" withBorder>
                                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                                    {entity.School && (
                                        <Box>
                                            <Text size="sm" c="dimmed">School</Text>
                                            {renderReferenceField(entity.School)}
                                        </Box>
                                    )}
                                    {entity.Rc && (
                                        <Box>
                                            <Text size="sm" c="dimmed">Responsibility Center</Text>
                                            {renderReferenceField(entity.Rc)}
                                        </Box>
                                    )}
                                    {entity.Manager && (
                                        <Box>
                                            <Text size="sm" c="dimmed">Manager</Text>
                                            {renderReferenceField(entity.Manager)}
                                        </Box>
                                    )}
                                    {entity.Assistant && (
                                        <Box>
                                            <Text size="sm" c="dimmed">Assistant</Text>
                                            {renderReferenceField(entity.Assistant)}
                                        </Box>
                                    )}
                                    {entity.Teacher && (
                                        <Box>
                                            <Text size="sm" c="dimmed">Teacher</Text>
                                            {renderReferenceField(entity.Teacher)}
                                        </Box>
                                    )}
                                    {entity.Principal && (
                                        <Box>
                                            <Text size="sm" c="dimmed">Principal</Text>
                                            {renderReferenceField(entity.Principal)}
                                        </Box>
                                    )}
                                    {entity.Principals && (
                                        <Box style={{ gridColumn: 'span 2' }}>
                                            <Text size="sm" c="dimmed">Principals</Text>
                                            <Group gap="xs" wrap="wrap">
                                                {entity.Principals.map((principal: any, index: number) => (
                                                    <ScimReferenceDisplay
                                                        key={index}
                                                        reference={principal}
                                                        variant="compact"
                                                        showType={true}
                                                    />
                                                ))}
                                            </Group>
                                        </Box>
                                    )}
                                    {entity.Owner && (
                                        <Box>
                                            <Text size="sm" c="dimmed">Owner</Text>
                                            {renderReferenceField(entity.Owner)}
                                        </Box>
                                    )}
                                    {entity.Members && (
                                        <Box style={{ gridColumn: 'span 2' }}>
                                            <Text size="sm" c="dimmed">Members</Text>
                                            <Group gap="xs" wrap="wrap">
                                                {entity.Members.map((member: any, index: number) => (
                                                    <ScimReferenceDisplay
                                                        key={index}
                                                        reference={member}
                                                        variant="compact"
                                                        showType={true}
                                                    />
                                                ))}
                                            </Group>
                                        </Box>
                                    )}
                                    {entity.Member && (
                                        <Box>
                                            <Text size="sm" c="dimmed">Member</Text>
                                            {renderReferenceField(entity.Member)}
                                        </Box>
                                    )}
                                </SimpleGrid>
                            </Paper>
                        </>
                    )}

                    {/* Arrays and Collections */}
                    {(entity.GradeLevels?.length || entity.Guardians?.length || entity.Students?.length || entity.Teachers?.length || entity.Managers?.length || entity.Labels?.length || entity.CoursePeriods?.length || entity.MarkingPeriods?.length || entity.Subjects?.length || entity.Sections?.length || entity.Ids?.length || entity.schemaExtensions?.length || entity.emails?.length || entity.phoneNumbers?.length) && (
                        <>
                            <Title order={5} mb="xs">Collections</Title>
                            <Paper p="md" mb="md" withBorder>
                                <SimpleGrid cols={1} spacing="md">
                                    {renderStringArray(entity.GradeLevels, 'Grade Levels')}
                                    {renderReferenceArray(entity.Guardians, 'Guardians')}
                                    {renderReferenceArray(entity.Students, 'Students')}
                                    {renderReferenceArray(entity.Teachers, 'Teachers')}
                                    {renderReferenceArray(entity.Managers, 'Managers')}
                                    {renderStringArray(entity.Labels, 'Labels')}
                                    {renderStringArray(entity.CoursePeriods, 'Course Periods')}
                                    {renderStringArray(entity.MarkingPeriods, 'Marking Periods')}
                                    {renderStringArray(entity.Subjects, 'Subjects')}
                                    {renderStringArray(entity.Sections, 'Sections')}
                                    {renderStringArray(entity.Ids, 'IDs')}
                                    {renderStringArray(entity.schemaExtensions, 'Schema Extensions')}
                                    {entity.emails?.length && (
                                        <Box>
                                            <Text size="sm" c="dimmed">Emails</Text>
                                            <Group gap="xs" wrap="wrap">
                                                {entity.emails.map((email: any, index: number) => (
                                                    <Badge
                                                        key={index}
                                                        variant="light"
                                                        color={email.primary ? 'blue' : 'gray'}
                                                        size="sm"
                                                    >
                                                        {email.value}
                                                    </Badge>
                                                ))}
                                            </Group>
                                        </Box>
                                    )}
                                    {entity.phoneNumbers?.length && (
                                        <Box>
                                            <Text size="sm" c="dimmed">Phone Numbers</Text>
                                            <Group gap="xs" wrap="wrap">
                                                {entity.phoneNumbers.map((phone: any, index: number) => (
                                                    <Badge
                                                        key={index}
                                                        variant="light"
                                                        color={phone.primary ? 'blue' : 'gray'}
                                                        size="sm"
                                                    >
                                                        {phone.value}
                                                    </Badge>
                                                ))}
                                            </Group>
                                        </Box>
                                    )}
                                </SimpleGrid>
                            </Paper>
                        </>
                    )}

                    {/* Boolean Fields */}
                    {(entity.Attendance !== undefined || entity.Gpa !== undefined || entity.Grades !== undefined || entity.District !== undefined || entity.Vacant !== undefined) && (
                        <>
                            <Title order={5} mb="xs">Flags</Title>
                            <Paper p="md" mb="md" withBorder>
                                <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
                                    {entity.Attendance !== undefined && renderBooleanField(entity.Attendance, 'Attendance')}
                                    {entity.Gpa !== undefined && renderBooleanField(entity.Gpa, 'GPA')}
                                    {entity.Grades !== undefined && renderBooleanField(entity.Grades, 'Grades')}
                                    {entity.District !== undefined && renderBooleanField(entity.District, 'District')}
                                    {entity.Vacant !== undefined && renderBooleanField(entity.Vacant, 'Vacant')}
                                </SimpleGrid>
                            </Paper>
                        </>
                    )}

                    {/* System Information */}
                    <Title order={5} mb="xs">System Information</Title>
                    <Paper p="md" mb="md" withBorder>
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                            {entity.EntityType && (
                                <Box>
                                    <Text size="sm" c="dimmed">Entity Type</Text>
                                    <Text>{getReferenceDisplay(entity.EntityType)}</Text>
                                </Box>
                            )}
                            {entity.Type && (
                                <Box>
                                    <Text size="sm" c="dimmed">Type</Text>
                                    <Text>{entity.Type}</Text>
                                </Box>
                            )}
                            {entity.SqlStatement && (
                                <Box style={{ gridColumn: 'span 2' }}>
                                    <Group gap="xs" mb={4}>
                                        <Text size="sm" c="dimmed">Sql Statement</Text>
                                        <Tooltip label={sqlCopied ? "Copied!" : "Copy SQL to clipboard"}>
                                            <ActionIcon
                                                size="xs"
                                                variant="subtle"
                                                onClick={() => handleCopySqlStatement(String(entity.SqlStatement))}
                                            >
                                                <IconCopy size={14} color={sqlCopied ? 'var(--mantine-color-green-6)' : undefined} />
                                            </ActionIcon>
                                        </Tooltip>
                                        <Tooltip label={sqlExpanded ? "Collapse SQL" : "Expand SQL to full size"}>
                                            <ActionIcon
                                                size="xs"
                                                variant="subtle"
                                                onClick={() => setSqlExpanded(!sqlExpanded)}
                                            >
                                                {sqlExpanded ? <IconArrowsMinimize size={14} /> : <IconArrowsMaximize size={14} />}
                                            </ActionIcon>
                                        </Tooltip>
                                    </Group>
                                    <Box
                                        mt="xs"
                                        style={{
                                            borderRadius: 4,
                                            maxHeight: sqlExpanded ? 'none' : '200px',
                                            overflow: 'auto',
                                        }}
                                    >
                                        <SyntaxHighlighter
                                            language="sql"
                                            style={colorScheme === 'dark' ? vscDarkPlus : vs}
                                            customStyle={{
                                                margin: 0,
                                                borderRadius: '4px',
                                                fontSize: '0.875rem',
                                            }}
                                        >
                                            {String(entity.SqlStatement)}
                                        </SyntaxHighlighter>
                                    </Box>
                                </Box>
                            )}
                            {entity.StudentCount !== undefined && (
                                <Box>
                                    <Text size="sm" c="dimmed">Student Count</Text>
                                    <Text>{entity.StudentCount}</Text>
                                </Box>
                            )}
                        </SimpleGrid>
                    </Paper>
                </Box>
            )}

            {activeTab === 'graph' && (
                <ReferencesGraph entity={entity} />
            )}

            {activeTab === 'raw' && (
                <Box
                    p="md"
                    ff="monospace"
                    fz="xs"
                    style={{
                        maxHeight: '600px',
                        overflow: 'auto',
                        borderRadius: 4,
                        border: '1px solid var(--mantine-color-default-border)',
                    }}
                >
                    <Text component="pre" m={0}>{(() => {
                        try {
                            const seen = new WeakSet();
                            const replacer = (_key: string, value: any) => {
                                if (typeof value === 'object' && value !== null) {
                                    if (seen.has(value)) {
                                        return '[Circular Reference]';
                                    }
                                    seen.add(value);
                                }
                                if (typeof value === 'function') return '[Function]';
                                if (typeof value === 'undefined') return '[Undefined]';
                                if (typeof value === 'symbol') return '[Symbol]';
                                return value;
                            };
                            return JSON.stringify(entity, replacer, 2);
                        } catch (error) {
                            return `Error serializing JSON: ${error instanceof Error ? error.message : 'Unknown error'}`;
                        }
                    })()}</Text>
                </Box>
            )}
        </Box>
    );
};
